import { useEffect, useRef, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { Delete, Upload } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";
import ResponsiveSelect from "../../../components/ui/ResponsiveSelect";
import BrandingLogo from "./BrandingLogo";
import type { BrandingDTO, SaveBrandingRequest } from "../../types/brandingDtos";

const ALL_SITES = "__all__";
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const useStyles = makeStyles((theme) => ({
    header: { padding: theme.spacing(3, 3, 0) },
    title: { padding: 0, margin: 0 },
    field: { marginTop: theme.spacing(2) },
    hint: { color: theme.palette.text.secondary, display: "block", marginTop: theme.spacing(0.5) },
    label: { display: "block", marginBottom: theme.spacing(0.5) },
    logoRow: { alignItems: "center", display: "flex", gap: theme.spacing(2) },
    colourHeader: { alignItems: "center", display: "flex", justifyContent: "space-between" },
    logoPreview: { maxHeight: 40, maxWidth: 160, objectFit: "contain", display: "block" },
    colours: { display: "flex", gap: theme.spacing(2) },
    colourColumn: { display: "flex", flexDirection: "column", flex: 1 },
    colourField: { alignItems: "center", display: "flex", gap: theme.spacing(1) },
    swatch: { border: "none", height: 40, padding: 0, width: 40, background: "none", cursor: "pointer" },
    error: { color: theme.palette.error.main, display: "block", marginTop: theme.spacing(0.5) },
    hidden: { display: "none" },
}));

interface BrandingEditorDialogProps {
    open: boolean;
    branding?: BrandingDTO | null;
    sites: any[];
    canManageAllSites: boolean;
    takenScopes: (string | null)[];
    isSaving?: boolean;
    onClose: () => void;
    onSave: (branding: SaveBrandingRequest) => void;
}

export default function BrandingEditorDialog({
    open,
    branding,
    sites,
    canManageAllSites,
    takenScopes,
    isSaving,
    onClose,
    onSave,
}: BrandingEditorDialogProps) {
    const classes = useStyles();
    const theme = useTheme();
    const fileInput = useRef<HTMLInputElement>(null);

    // The standard WorkNest colours, which branding starts from and can reset to.
    const defaultPrimary = theme.palette.primary.main;
    const defaultSecondary = theme.palette.secondary.main;

    const [scope, setScope] = useState<string>(ALL_SITES);
    const [primaryColour, setPrimaryColour] = useState(defaultPrimary);
    const [secondaryColour, setSecondaryColour] = useState(defaultSecondary);
    const [isActive, setIsActive] = useState(true);
    const [logo, setLogo] = useState<File | null>(null);
    const [removeLogo, setRemoveLogo] = useState(false);
    const [logoError, setLogoError] = useState("");
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;

        // Don't preselect a scope the user can't save, or the first save 401s.
        const firstFreeSite = sites.find((s: any) => !takenScopes.includes(s.externalId))?.externalId;

        setScope(
            branding?.siteExternalId
                ? branding.siteExternalId
                : canManageAllSites && !takenScopes.includes(null)
                  ? ALL_SITES
                  : firstFreeSite ?? ALL_SITES
        );
        setPrimaryColour(branding?.primaryColour ?? defaultPrimary);
        setSecondaryColour(branding?.secondaryColour ?? defaultSecondary);
        setIsActive(branding ? branding.isActive : true);
        setLogo(null);
        setRemoveLogo(false);
        setLogoError("");
    }, [open, branding, defaultPrimary, defaultSecondary, canManageAllSites, sites, takenScopes]);

    useEffect(() => {
        if (!logo) {
            setLogoPreview(null);
            return;
        }

        const objectUrl = URL.createObjectURL(logo);
        setLogoPreview(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [logo]);

    const pickLogo = (file?: File) => {
        if (!file) return;

        if (!/\.(png|jpe?g)$/i.test(file.name)) {
            setLogoError("Choose a PNG or JPEG image.");
            return;
        }

        if (file.size > LOGO_MAX_BYTES) {
            setLogoError("Choose an image under 2MB.");
            return;
        }

        setLogoError("");
        setRemoveLogo(false);
        setLogo(file);
    };

    const clearLogo = () => {
        setLogo(null);
        setLogoError("");
        if (fileInput.current) fileInput.current.value = "";
        if (branding?.logoReference) setRemoveLogo(true);
    };

    const showExistingLogo = !!branding?.logoReference && !logo && !removeLogo;
    const hasLogo = !!logo || showExistingLogo;
    const invalidColour = [primaryColour, secondaryColour].some((c) => !!c.trim() && !HEX.test(c.trim()));
    const nothingToApply = isActive && !hasLogo && !primaryColour.trim() && !secondaryColour.trim();
    const canSave = !invalidColour && !nothingToApply && !logoError && !isSaving;

    const isCustomColours =
        primaryColour.toLowerCase() !== defaultPrimary.toLowerCase() ||
        secondaryColour.toLowerCase() !== defaultSecondary.toLowerCase();

    const resetColours = () => {
        setPrimaryColour(defaultPrimary);
        setSecondaryColour(defaultSecondary);
    };

    const scopeOptions = [
        // Only offered to an admin for every site, since it reaches all of them.
        ...(canManageAllSites ? [{ value: ALL_SITES, label: "All sites without their own branding" }] : []),
        ...sites.map((s: any) => ({ value: s.externalId, label: s.name })),
    ].filter((option) => {
        const scopeId = option.value === ALL_SITES ? null : option.value;
        const currentScope = branding ? branding.siteExternalId ?? null : undefined;
        return scopeId === currentScope || !takenScopes.includes(scopeId);
    });

    const save = () =>
        onSave({
            siteExternalId: scope === ALL_SITES ? null : scope,
            primaryColour: primaryColour.trim() || null,
            secondaryColour: secondaryColour.trim() || null,
            isActive,
            logo,
            removeLogo,
        });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <div className={classes.header}>
                <DialogTitle className={classes.title}>{branding ? "Edit branding" : "Add branding"}</DialogTitle>
                <DialogContentText className={classes.title}>
                    Add your logo and colours. Without branding, risk assessments use the standard WorkNest look.
                </DialogContentText>
            </div>
            <DialogContent>
                <div className={classes.field}>
                    <ResponsiveSelect
                        size="small"
                        required
                        fullWidth
                        label="Applies to"
                        options={scopeOptions}
                        optionValueKey="value"
                        optionLabelKey="label"
                        value={scope}
                        onChange={(e: any) => setScope(e.target.value)}
                        disabled={!!branding}
                    />
                    <Typography variant="caption" className={classes.hint}>
                        {branding
                            ? "The sites this branding applies to can't be changed after it's created."
                            : "A site with its own branding turned on always uses that instead."}
                    </Typography>
                </div>

                <div className={classes.field}>
                    <Typography variant="subtitle2" className={classes.label}>
                        Logo
                    </Typography>
                    <div className={classes.logoRow}>
                        {logoPreview && <img className={classes.logoPreview} src={logoPreview} alt={logo?.name} />}
                        {showExistingLogo && (
                            <BrandingLogo reference={branding?.logoReference} fileName={branding?.logoFileName} />
                        )}
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<Upload />}
                            onClick={() => fileInput.current?.click()}
                        >
                            {hasLogo ? "Replace" : "Upload"}
                        </Button>
                        {hasLogo && (
                            <Button color="primary" size="small" startIcon={<Delete />} onClick={clearLogo}>
                                Remove
                            </Button>
                        )}
                        <input
                            ref={fileInput}
                            className={classes.hidden}
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={(e) => pickLogo(e.target.files?.[0])}
                        />
                    </div>
                    {logoError ? (
                        <Typography variant="caption" className={classes.error}>
                            {logoError}
                        </Typography>
                    ) : (
                        <Typography variant="caption" className={classes.hint}>
                            PNG or JPEG, up to 2MB. A wide logo on a transparent background works best.
                        </Typography>
                    )}
                </div>

                <div className={classes.field}>
                    <div className={classes.colourHeader}>
                        <Typography variant="subtitle2" className={classes.label}>
                            Colours
                        </Typography>
                        {isCustomColours && (
                            <Button size="small" color="primary" onClick={resetColours}>
                                Reset to WorkNest colours
                            </Button>
                        )}
                    </div>
                    <div className={classes.colours}>
                        <div className={classes.colourColumn}>
                            <div className={classes.colourField}>
                                <input
                                    className={classes.swatch}
                                    type="color"
                                    aria-label="Pick a primary colour"
                                    value={HEX.test(primaryColour) ? primaryColour : defaultPrimary}
                                    onChange={(e) => setPrimaryColour(e.target.value)}
                                />
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    label="Primary"
                                    placeholder={defaultPrimary}
                                    value={primaryColour}
                                    onChange={(e) => setPrimaryColour(e.target.value)}
                                    slotProps={{ htmlInput: { maxLength: 7 } }}
                                />
                            </div>
                            <Typography variant="caption" className={classes.hint}>
                                The line under the title, and the line under each section heading.
                            </Typography>
                        </div>
                        <div className={classes.colourColumn}>
                            <div className={classes.colourField}>
                                <input
                                    className={classes.swatch}
                                    type="color"
                                    aria-label="Pick a secondary colour"
                                    value={HEX.test(secondaryColour) ? secondaryColour : defaultSecondary}
                                    onChange={(e) => setSecondaryColour(e.target.value)}
                                />
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    label="Secondary"
                                    placeholder={defaultSecondary}
                                    value={secondaryColour}
                                    onChange={(e) => setSecondaryColour(e.target.value)}
                                    slotProps={{ htmlInput: { maxLength: 7 } }}
                                />
                            </div>
                            <Typography variant="caption" className={classes.hint}>
                                The heading band across the top of each table.
                            </Typography>
                        </div>
                    </div>
                    {invalidColour ? (
                        <Typography variant="caption" className={classes.error}>
                            Enter a hex value, for example {defaultPrimary}.
                        </Typography>
                    ) : (
                        <Typography variant="caption" className={classes.hint}>
                            {isCustomColours
                                ? "Risk scores stay red, amber and green whatever you choose."
                                : "These are the standard WorkNest colours. Change them to use your own."}
                        </Typography>
                    )}
                </div>

                <div className={classes.field}>
                    <Tooltip title="Turn this off to go back to the standard WorkNest look without losing your logo and colours.">
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    color="secondary"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                            }
                            label="Branding on"
                        />
                    </Tooltip>
                    {nothingToApply && (
                        <Typography variant="caption" className={classes.error}>
                            Add a logo or a colour before turning branding on.
                        </Typography>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={save} variant="contained" color="primary" disabled={!canSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
